import { useCallback, useRef, useState } from "react";

import { datadogRum } from "@datadog/browser-rum";
import type { StatsigClient, StatsigUser } from "@statsig/react-bindings";

import type {
  StatsigCustomAttrs,
  StatsigUserContextValue,
} from "./StatsigUserProvider.types";
import { hasPrefetchedValuesFor } from "./StatsigUserProvider.utils";

type UseStatsigUserWriterArgs = {
  client: StatsigClient;
  disabled: boolean;
  ssoId: string | undefined;
};

/**
 * The write logic behind `StatsigUserProvider`: reconciles the live Statsig
 * user toward requested `custom` attributes (`setAttributes`) and `locale`
 * (`setLocale`). Both are `async`, so callers can either fire-and-forget
 * (`void setAttributes(...)`) or await settlement (`await setAttributes(...)`)
 * then read the now-updated experiment.
 *
 * Every write is prefetch-then-swap. On this bootstrapped client,
 * `updateUserAsync` switches the user at once but keeps serving the server
 * bootstrap until `/v1/initialize` returns (the SDK's cache key ignores
 * `custom`), tagging every read in between `BootstrapPartialUserMatch` —
 * Statsig's "invalid bootstrap" health check. Instead, `prefetchData` fetches
 * the next user's values while the current user stays live (reads stay valid
 * `Bootstrap`), then `updateUserSync` switches onto them in one step
 * (`Prefetch`) — the same single network call. Only if the prefetch comes back
 * empty does it fall back to `updateUserAsync`, so the user still switches.
 *
 * Overlapping writes merge onto the pending user rather than the still-live one,
 * and only the latest switch is applied. An earlier caller resolves once that
 * latest switch settles, since it carries the earlier patch too.
 *
 * `isSyncing` is a counter-based "a switch is in flight" flag. The SDK's
 * `useStatsigClient().isLoading` stays false during a prefetch (only the
 * `updateUserAsync` fallback flips it), so `isSyncing` is the signal to use.
 * A caller reading an experiment targeted on these attrs should
 * `await setAttributes(...)` (or wait out `isSyncing`) so the read sees the
 * switched user's values.
 *
 * For bots (`disabled`) it writes nothing.
 */
export const useStatsigUserWriter = ({
  client,
  disabled,
  ssoId,
}: UseStatsigUserWriterArgs): Omit<StatsigUserContextValue, "disabled"> => {
  const [isSyncing, setIsSyncing] = useState(false);
  // Counts concurrent switches so overlapping writes (listing sync + zero-down +
  // a quote refetch) don't flip `isSyncing` false while one is still in flight.
  const inFlightRef = useRef(0);
  // The user the latest in-flight switch is moving to. New writes merge onto it
  // rather than the still-live user, so overlapping patches don't drop each other.
  const pendingUserRef = useRef<StatsigUser | null>(null);
  // The latest switch; superseded callers wait on it before resolving.
  const latestSwitchRef = useRef<Promise<void> | null>(null);

  /** Resolves once the latest in-flight switch, if any, has settled. */
  const waitForLatestSwitch = useCallback(async () => {
    let awaited: Promise<void> | null = null;
    while (inFlightRef.current > 0 && latestSwitchRef.current !== awaited) {
      awaited = latestSwitchRef.current;
      await awaited;
    }
  }, []);

  const switchUser = useCallback(
    async (nextUser: StatsigUser) => {
      pendingUserRef.current = nextUser;
      inFlightRef.current += 1;
      setIsSyncing(true);

      const run = (async () => {
        try {
          await client.dataAdapter
            .prefetchData(nextUser)
            .catch((error: unknown) => datadogRum.addError(error));
          // A newer write started while this one was fetching. It merged this
          // patch, so its switch covers both.
          if (pendingUserRef.current !== nextUser) {
            return;
          }
          pendingUserRef.current = null;
          if (hasPrefetchedValuesFor(client, nextUser)) {
            client.updateUserSync(nextUser, {
              disableBackgroundCacheRefresh: true,
            });
          } else {
            // The prefetch came back empty (network error, blocked Statsig
            // domain). Still switch — callers like `BookListingContext` wait for
            // the user to reflect their patch — via `updateUserAsync`, which
            // switches now and retries the fetch. Its reads are flagged until
            // values land, but only on this failure path.
            await client.updateUserAsync(nextUser);
          }
        } catch (error) {
          datadogRum.addError(error);
        } finally {
          inFlightRef.current -= 1;
          if (inFlightRef.current === 0) {
            setIsSyncing(false);
          }
        }
      })();
      latestSwitchRef.current = run;

      await run;
      await waitForLatestSwitch();
    },
    [client, waitForLatestSwitch],
  );

  const setAttributes = useCallback(
    async (patch: StatsigCustomAttrs) => {
      if (disabled) {
        return;
      }

      // Diff against the user the latest switch is heading to, or the live user
      // when nothing is in flight.
      const base = pendingUserRef.current ?? client.getContext().user;
      const patchKeys = Object.keys(patch) as (keyof StatsigCustomAttrs)[];

      // Skip the write when that user already reflects the patch + identity. This
      // is what keeps `setAttributes` safe to call from an effect: a redundant
      // call is a no-op instead of another switch. If the switch carrying the
      // patch is still in flight, wait for it so an awaiting caller reads
      // settled values.
      const attrsApplied = patchKeys.every(
        (key) => base.custom?.[key] === patch[key],
      );
      const identityMatches = ssoId == null || base.userID === ssoId;
      if (attrsApplied && identityMatches) {
        await waitForLatestSwitch();
        return;
      }

      await switchUser({
        ...base,
        ...(ssoId != null && base.userID !== ssoId ? { userID: ssoId } : {}),
        custom: { ...base.custom, ...patch },
      });
    },
    [client, disabled, ssoId, switchUser, waitForLatestSwitch],
  );

  // `locale` is a top-level StatsigUser field (not a `custom` attribute), so it gets its own setter
  const setLocale = useCallback(
    async (locale: string) => {
      if (disabled) {
        return;
      }

      const base = pendingUserRef.current ?? client.getContext().user;
      if (base.locale === locale) {
        await waitForLatestSwitch();
        return;
      }

      await switchUser({ ...base, locale });
    },
    [client, disabled, switchUser, waitForLatestSwitch],
  );

  return { isSyncing, setAttributes, setLocale };
};
